import React from 'react';
import { Badge, Box, Card, Divider, Grid, Group, NumberInput, Stack, Tabs, Text, ThemeIcon } from '@mantine/core';
import { IconAdjustments, IconCertificate, IconTrendingUp } from '@tabler/icons-react';
import type { IVariablesServicios } from '../../../modelos/CotizadorServicios/entidades/IConfiguracionServicios';

interface FormularioVariablesServiciosVistaProps {
    variables: IVariablesServicios;
    alCambiar: (campo: keyof IVariablesServicios, valor: number | string) => void;
}

interface TarjetaProps {
    titulo: string;
    etiqueta: string;
    icono: React.ReactNode;
    children: React.ReactNode;
}

const Tarjeta: React.FC<TarjetaProps> = ({ titulo, etiqueta, icono, children }) => (
    <Card shadow="none" p="lg" radius="md" withBorder h="100%">
        <Group justify="space-between" mb="xs">
            <Group gap="xs">
                <ThemeIcon variant="light" color="primary" radius="xl" size="md">{icono}</ThemeIcon>
                <Text fw={600} size="lg">{titulo}</Text>
            </Group>
            <Badge color="primary" variant="light" radius="xl" style={{ textTransform: 'none' }}>{etiqueta}</Badge>
        </Group>
        <Divider mb="md" />
        {children}
    </Card>
);

/** Campos de ascensores y de escaleras divididos a la mitad; en pantallas pequeñas, en pestañas. */
const DivisionPorFamilia: React.FC<{ ascensores: React.ReactNode; escaleras: React.ReactNode }> = ({ ascensores, escaleras }) => (
    <>
        <Box visibleFrom="sm">
            <div style={{ display: 'flex', gap: '24px' }}>
                <Box style={{ flex: 1 }}>
                    <Text fw={600} size="sm" mb="sm" ta="center">Ascensores</Text>
                    <Stack gap="xs">{ascensores}</Stack>
                </Box>
                <Divider orientation="vertical" my="lg" />
                <Box style={{ flex: 1 }}>
                    <Text fw={600} size="sm" mb="sm" ta="center">Escaleras, Rampas y Andenes</Text>
                    <Stack gap="xs">{escaleras}</Stack>
                </Box>
            </div>
        </Box>
        <Box hiddenFrom="sm">
            <Tabs defaultValue="ascensores" variant="outline">
                <Tabs.List mb="sm">
                    <Tabs.Tab value="ascensores">Ascensores</Tabs.Tab>
                    <Tabs.Tab value="escaleras">Escaleras</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="ascensores"><Stack gap="xs">{ascensores}</Stack></Tabs.Panel>
                <Tabs.Panel value="escaleras"><Stack gap="xs">{escaleras}</Stack></Tabs.Panel>
            </Tabs>
        </Box>
    </>
);

/** Pestaña "Tarifas y Variables": se guarda completa con la barra de cambios sin guardar. */
export const FormularioVariablesServiciosVista: React.FC<FormularioVariablesServiciosVistaProps> = ({ variables, alCambiar }) => {
    const vacio = (campo: keyof IVariablesServicios) => String(variables[campo] ?? '') === '';
    const campo = (nombre: keyof IVariablesServicios) => ({
        size: 'xs' as const,
        min: 0,
        allowNegative: false,
        withAsterisk: true,
        hideControls: true,
        value: variables[nombre],
        onChange: (valor: number | string) => alCambiar(nombre, valor),
        error: vacio(nombre)
    });
    const moneda = { prefix: '$ ', thousandSeparator: '.', decimalSeparator: ',' };
    const porcentaje = { suffix: ' %', decimalScale: 2 };

    return (
        <Grid gutter="lg">
            <Grid.Col span={{ base: 12, md: 6 }}>
                <Tarjeta titulo="Tarifas y Costos Operativos" etiqueta="Tarifas" icono={<IconAdjustments size={18} />}>
                    <DivisionPorFamilia
                        ascensores={<>
                            <NumberInput label="Tarifa hora hombre" {...campo('TarifaHoraAscensor')} {...moneda} />
                            <NumberInput label="Horas por módulo de visita" {...campo('HorasPorModulo')} />
                            <NumberInput label="Mínimo de paradas" {...campo('MinimoParadas')} />
                            <NumberInput label="Costo por parada" {...campo('CostoPorParada')} {...moneda} />
                        </>}
                        escaleras={<>
                            <NumberInput label="Tarifa hora hombre" {...campo('TarifaHoraEscalera')} {...moneda} />
                            <NumberInput label="Factor escalera" {...campo('FactorEscalera')} decimalScale={3} />
                        </>}
                    />
                </Tarjeta>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
                <Tarjeta titulo="Márgenes y Variables Comerciales" etiqueta="Generales" icono={<IconTrendingUp size={18} />}>
                    <Stack gap="md">
                        <DivisionPorFamilia
                            ascensores={<NumberInput label="Margen sobre el costo" {...campo('MargenAscensores')} {...porcentaje} />}
                            escaleras={<NumberInput label="Margen sobre el costo" {...campo('MargenEscaleras')} {...porcentaje} />}
                        />
                        <Divider label="Aplican a ambas familias" labelPosition="center" />
                        <Grid>
                            <Grid.Col span={{ base: 12, sm: 4 }}>
                                <NumberInput label="CIF" {...campo('PorcentajeCif')} {...porcentaje} />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, sm: 4 }}>
                                <NumberInput label="Costos especiales" description="Ajuste administrativo" {...campo('PorcentajeCostosEspeciales')} {...porcentaje} />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, sm: 4 }}>
                                <NumberInput label="IVA" {...campo('PorcentajeIva')} {...porcentaje} />
                            </Grid.Col>
                        </Grid>
                    </Stack>
                </Tarjeta>
            </Grid.Col>

            <Grid.Col span={12}>
                <Tarjeta titulo="Planes de Mantenimiento" etiqueta="Planes" icono={<IconCertificate size={18} />}>
                    <Grid>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <NumberInput label="Factor plan Conekta" description="Sobre el precio final del equipo" {...campo('FactorPlanConekta')} decimalScale={3} />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <NumberInput label="Factor plan Conekta Plus" description="Sobre el precio final del equipo" {...campo('FactorPlanConektaPlus')} decimalScale={3} />
                        </Grid.Col>
                    </Grid>
                </Tarjeta>
            </Grid.Col>
        </Grid>
    );
};
